/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Checkbox, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { useTranslation } from 'react-i18next';
import { getButtonStates } from '../../../../utils/permissionService';

interface UserRightsTableProps {
  selectedModules: number[];
  selectedCategories: { id: number; name: string; moduleId: number; moduleName: string }[];
  selectedTypes: {name_label: string; id: number; name: string; categoryId: number; categoryName: string; moduleId: number; moduleName: string }[];
  moduleData: any;
  onRemove: (typeKey: string, subcategories?: any, moduleObj?: any, categoryObj?: any) => void;
  showHeader?: boolean;
  onGrantAccess?: (permissions: any) => void;
  apiActions?: any[];
}



const UserRightsTable: React.FC<UserRightsTableProps> = ({ selectedModules, selectedCategories, selectedTypes, moduleData, onRemove, apiActions = [], showHeader = true, onGrantAccess }) => {
  const {t} = useTranslation('user_config');
  const { canEdit, canAdd } = getButtonStates('Settings', 'Configuration', 'User');

  // Store props in local state
  const [localSelectedModules, setLocalSelectedModules] = useState<number[]>(selectedModules);
  const [localSelectedCategories, setLocalSelectedCategories] = useState(selectedCategories);
  const [localSelectedTypes, setLocalSelectedTypes] = useState(selectedTypes);
  const [localModuleData, setLocalModuleData] = useState(moduleData);

  useEffect(() => {
    setLocalSelectedModules(selectedModules);
  }, [selectedModules]);
  useEffect(() => {
    setLocalSelectedCategories(selectedCategories);
  }, [selectedCategories]);
  useEffect(() => {
    setLocalSelectedTypes(selectedTypes);
  }, [selectedTypes]);
  useEffect(() => {
    setLocalModuleData(moduleData);
  }, [moduleData]);

  const permissionKeys = React.useMemo(() => ['Add', 'Edit', 'View', 'Print'], []);

  const permissionLabels = [t('UserRights.Add'), t('UserRights.Edit'), t('UserRights.View'), t('UserRights.Print')];
  const getAllCategoryIds = (mod: any) => (mod.categories || []).map((cat: any) => cat.id);
  const getAllTypeIds = (cat: any) => (cat.types || []).map((type: any) => type.id);
  const allRows: any[] = React.useMemo(() => {
    const rows: any[] = [];
    localModuleData.objects.forEach((mod: any) => {
      if (!localSelectedModules.includes(mod.id)) return;
      const moduleCategories = mod.categories || [];
      const allCatIds = getAllCategoryIds(mod);
      const selectedCatObjs = localSelectedCategories.filter((cat) => cat.moduleId === mod.id);
      const allCatsSelected = allCatIds.length > 0 && allCatIds.every((catId: number) => 
        selectedCatObjs.some((cat) => cat.id === catId)
      );
      let allTypesInModuleSelected = true;
      if (allCatsSelected) {
        for (const cat of moduleCategories) {
          const typeIds = getAllTypeIds(cat);
          if (typeIds.length === 0) continue; 
          const selectedTypeObjs = localSelectedTypes.filter((type) => 
            type.categoryId === cat.id && type.moduleId === mod.id
          );
          if (!typeIds.every((tid: number) => selectedTypeObjs.some((t) => t.id === tid))) {
            allTypesInModuleSelected = false;
            break;
          }
        }
      } else {
        allTypesInModuleSelected = false;
      }
      if (allCatsSelected && allTypesInModuleSelected) {
        rows.push({
          type: 'module',
          name: mod.name,
          name_label: mod.name_label,
          typeKey: `module-${mod.name}`,
        });
        return; 
      }
      moduleCategories.forEach((cat: any) => {
        const catSelected = localSelectedCategories.some(
          (catObj) => catObj.name === cat.name && catObj.moduleName === mod.name
        );
        if (!catSelected) return; 
        const catTypeIds = getAllTypeIds(cat);
        const selectedTypeObjs = localSelectedTypes.filter((type) => 
          type.categoryId === cat.id && type.moduleId === mod.id
        );
        const allTypesInCategorySelected = catTypeIds.length > 0 && 
          catTypeIds.every((tid: number) => selectedTypeObjs.some((t) => t.id === tid));
        if (allTypesInCategorySelected) {
          rows.push({
            type: 'category',
            name: cat.name,
            name_label: cat.name_label,
            typeKey: `category-${mod.name}-${cat.name}`,
          });
        } else {
          const selectedSubcategories = localSelectedTypes.filter((typeObj) =>
            typeObj.categoryName === cat.name &&
            typeObj.moduleName === mod.name
          );
          selectedSubcategories.forEach((selectedType) => {
            rows.push({
              type: 'subcategory',
              name: selectedType.name,
              name_label: selectedType.name_label,
              typeKey: `subcategory-${mod.name}-${cat.name}-${selectedType.name}`,
            });
          });
        }
      });
    });
    return rows;
  }, [localSelectedModules, localSelectedCategories, localSelectedTypes, localModuleData]);

  const getTypeAndColor = (row: any) => {
    if (row.type === 'module') return { label: 'Module', color: '#2F80ED' };
    if (row.type === 'category') return { label: 'Category', color: '#27AE60' };
    return { label: 'Subcategory', color: '#F2C94C' };
  };

  const [rowPermissions, setRowPermissions] = useState<any>({});
  const initializedRef = useRef(false);
  const previousApiActionsRef = useRef<any>(null);  

  useEffect(() => {
    setRowPermissions((prev: any) => {
      const validKeys = new Set(allRows.map((row) => row.typeKey));
      const cleaned = Object.fromEntries(
        Object.entries(prev || {}).filter(([key]) => validKeys.has(key))
      );
      return cleaned;
    });
  }, [allRows]);

  useEffect(() => {
    // Deep comparison to check if apiActions actually changed
    const apiActionsChanged = JSON.stringify(previousApiActionsRef.current) !== JSON.stringify(apiActions);
    
    // Check if there are new rows that don't have permissions yet
    const hasNewRows = allRows.some(row => !rowPermissions[row.typeKey]);
    
    // Only reset permissions if not initialized yet OR if apiActions actually changed OR if there are new rows
    if (!initializedRef.current || apiActionsChanged || hasNewRows) {
      const newPerms: any = { ...rowPermissions };
      allRows.forEach(row => {
        // Skip if this row already has permissions and apiActions didn't change (on first init no rows are skipped)
        if (newPerms[row.typeKey] && !apiActionsChanged) {
          return;
        }
        
        let apiPerm = null;

        if (apiActions && Array.isArray(apiActions)) {
          if (row.type === 'module') {
            apiPerm = apiActions.find((a: any) => a.type === 'module' && a.Name === row.name);
          } else if (row.type === 'category') {
            apiPerm = apiActions.find((a: any) => a.type === 'category' && a.Name === row.name);
          } else if (row.type === 'subcategory') {
            apiPerm = apiActions.find((a: any) => {
              return a.type === 'type' && a.Name === row.name;
            });
          

            // Defensive: ensure actions is always an array (only if apiPerm exists)
            if (apiPerm && !Array.isArray(apiPerm?.actions)) {
              apiPerm = { ...apiPerm, actions: [] };
            }
          }
        }
        if (apiPerm) {
          const permsObj: Record<string, boolean> = {};
          let actionsArr: string[] = [];
          if (row.type === 'subcategory') {
            if (Array.isArray(apiPerm.action)) {
              if (typeof apiPerm.action[0] === 'string') {
                actionsArr = apiPerm.action.map((a: string) => a.toLowerCase());
              } else if (typeof apiPerm.action[0] === 'object') {
                actionsArr = apiPerm.action.map((a: any) => {
                  if (a.name) return a.name.toLowerCase();
                  if (a.action) return a.action.toLowerCase();
                  return '';
                });
              }
            } else if (typeof apiPerm.action === 'string') {
              actionsArr = [apiPerm.action.toLowerCase()];
            }
          } else if (Array.isArray(apiPerm.actions)) {
            if (typeof apiPerm.actions[0] === 'string') {
              actionsArr = apiPerm.actions.map((a: string) => a.toLowerCase());
            } else if (typeof apiPerm.actions[0] === 'object') {
              actionsArr = apiPerm.actions.map((a: any) => {
                if (a.name) return a.name.toLowerCase();
                if (a.action) return a.action.toLowerCase();
                return '';
              });
            }
          }
          permissionKeys.forEach((key) => {
            permsObj[key] = actionsArr.includes(key.toLowerCase());
          });
          newPerms[row.typeKey] = {
            male: typeof apiPerm.male === 'boolean' ? apiPerm.male : true,
            female: typeof apiPerm.female === 'boolean' ? apiPerm.female : true,
            permissions: permsObj,
          };
        } else {
        // By default, all checkboxes selected
          newPerms[row.typeKey] = {
            male: true,
            female: true,
            permissions: permissionKeys.reduce((acc, key) => {
              acc[key] = true;
              return acc;
            }, {} as Record<string, boolean>),
          };
        }
      });
    
      setRowPermissions(newPerms);
      initializedRef.current = true;
      previousApiActionsRef.current = apiActions;
    }
  }, [allRows, apiActions, permissionKeys, rowPermissions]);

  const handleGenderChange = (typeKey: string, gender: 'male' | 'female') => {
    setRowPermissions((prev: any) => {
      const current = prev[typeKey] || { male: false, female: false, permissions: {} };
      return {
        ...prev,
        [typeKey]: {
          ...current,
          [gender]: !current[gender],
        },
      };
    });
  };
  const handlePermissionChange = (typeKey: string, perm: string) => {
    setRowPermissions((prev: any) => ({
      ...prev,
      [typeKey]: {
        ...prev[typeKey],
        permissions: {
          ...prev[typeKey]?.permissions,
          [perm]: !prev[typeKey]?.permissions[perm],
        },
      },
    }));
  };


  const handleAllChange = (typeKey: string) => {
    setRowPermissions((prev: any) => {
      const allChecked = permissionKeys.every((perm) => prev[typeKey]?.permissions[perm]);
      const newPerms = { ...prev[typeKey]?.permissions };
      permissionKeys.forEach((perm) => {
        newPerms[perm] = !allChecked;
      });
      return {
        ...prev,
        [typeKey]: {
          ...prev[typeKey],
          permissions: newPerms,
        },
      };
    });
  };

  const handleGrantAccess = () => {
    const permissions: any = { modules: [] };
    localModuleData.objects.forEach((mod: any) => {
      if (!localSelectedModules.includes(mod.id)) return;
      const moduleRowKey = `module-${mod.name}`;
      const modulePerms = rowPermissions[moduleRowKey];
      const modObj: any = { name: mod.name, submodules: [] };
      (mod.categories || []).forEach((cat: any) => {
        const catSelected = localSelectedCategories.some(
          (catObj) => catObj.name === cat.name && catObj.moduleName === mod.name
        );
        if (!catSelected) return;
        const categoryRowKey = `category-${mod.name}-${cat.name}`;
        const categoryPerms = rowPermissions[categoryRowKey];
        const selectedTypeIdsForCategory = new Set(
          localSelectedTypes
            .filter((typeObj) => typeObj.categoryId === cat.id && typeObj.moduleId === mod.id)
            .map((typeObj) => typeObj.id)
        );
        const catObj: any = { name: cat.name, type: [] };
        (cat.types || []).forEach((type: any) => {
          if (!modulePerms && !categoryPerms && !selectedTypeIdsForCategory.has(type.id)) {
            return;
          }
          const typeKey = `subcategory-${mod.name}-${cat.name}-${type.name}`;
          let perms = rowPermissions[typeKey];
          if (modulePerms) perms = modulePerms;
          else if (categoryPerms) perms = categoryPerms;
          // Ensure perms is always defined with default values
          perms = perms || { male: false, female: false, permissions: {} };
          const typePerms: any[] = [];
          Object.entries(perms.permissions).forEach(([perm, checked]) => {
            if (checked) {
              typePerms.push({
                access: perm,
                male: perms.male,
                female: perms.female,
              });
            }
          });
          catObj.type.push({ 
            name: type.name, 
            id: type.id, 
            permissions: typePerms 
          });
        });
        if (catObj.type.length > 0) {
          modObj.submodules.push(catObj);
        }
      });
      if (modObj.submodules.length > 0) {
        permissions.modules.push(modObj);
      }
    });

    
    if (onGrantAccess) onGrantAccess(permissions);
  };

  if (allRows.length === 0) {
    return null;
  }

  const CustomCheckboxIcon = (
    <Box sx={{ border: '2px solid #B6E2C2', width: 24, height: 24, bgcolor: '#E6F7EA', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CheckIcon sx={{ color: '#4CAF50', fontSize: 20 }} />
    </Box>
  );
  const CustomCheckboxUncheckedIcon = (
    <Box sx={{ border: '2px solid #B6E2C2', width: 24, height: 24, bgcolor: '#fff', borderRadius: 1 }} />
  );

  return (
    <Box>
      <TableContainer component={Paper} sx={{ mt: 2, borderRadius: 3 }} elevation={0}>
        <Table>
          {showHeader && (
            <TableHead>
              <TableRow sx={{ bgcolor: '#FAFAFA' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: 24, border: 'none' }}></TableCell>
                <TableCell align="left" sx={{ fontWeight: 500, fontSize: 18, border: 'none', px: 1, py:2 }}>{t('UserRights.Gender')}</TableCell>
                <TableCell align="left" sx={{ fontWeight: 500, fontSize: 18, border: 'none', px: 1, py:2 }}>{t('UserRights.All')}</TableCell>
                {permissionLabels.map((label, index) => (
                  <TableCell key={permissionKeys[index]} align="left" sx={{ fontWeight: 500, fontSize: 18, border: 'none', px: 1, py:2 }}>{label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
          )}
          <TableBody>
            {allRows.map((row: any) => {
              const { color } = getTypeAndColor(row);
              const perms = rowPermissions[row.typeKey] || { male: false, female: false, permissions: {} };
              return (
                <TableRow key={row.typeKey}>
                  <TableCell sx={{ border: 'none', p: 0 }}>
                    <Box sx={{
                      border: `2px solid ${color}`,
                      borderRadius: 2,
                      px: 2,
                      py: 0,
                      width: '200px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontWeight: 600,
                      fontSize: 18,
                      bgcolor: '#fff',
                      mr: 2,
                    }}>
                      <Typography>
                        {row.name_label || row.name}
                      </Typography>
                      <IconButton
                        size="small"
                        sx={{ ml: 1 }}
                        onClick={() => {
                          let subcategories = [];
                          if (row.type === 'module') {
                            const modObj = localModuleData.objects.find((m: any) => 'module-' + m.name === row.typeKey);
                            if (modObj && Array.isArray(modObj.categories)) {
                              subcategories = modObj.categories.flatMap((cat: any) => cat.types || []);
                            }
                            setLocalSelectedModules((prev) => prev.filter((modId) => {
                              const modObj = localModuleData.objects.find((m: any) => m.id === modId);
                              return 'module-' + (modObj ? modObj.name : '') !== row.typeKey;
                            }));
                            setLocalSelectedCategories((prev) => prev.filter((cat) => 'category-' + cat.moduleName + '-' + cat.name !== row.typeKey));
                            setLocalSelectedTypes((prev) => prev.filter((type) => 'subcategory-' + type.moduleName + '-' + type.categoryName + '-' + type.name !== row.typeKey));
                            onRemove(row.typeKey, subcategories, modObj);
                          } else if (row.type === 'category') {
                            const modObj = localModuleData.objects.find((m: any) => 'category-' + m.name + '-' + row.name === row.typeKey);
                            const catObj = modObj && Array.isArray(modObj.categories) ? (modObj.categories || []).find((cat: any) => cat.name === row.name) : null;
                            if (catObj && Array.isArray(catObj.types)) {
                              subcategories = catObj.types;
                            }
                            setLocalSelectedCategories((prev) => prev.filter((cat) => 'category-' + cat.moduleName + '-' + cat.name !== row.typeKey));
                            setLocalSelectedTypes((prev) => prev.filter((type) => 'subcategory-' + type.moduleName + '-' + type.categoryName + '-' + type.name !== row.typeKey));
                            onRemove(row.typeKey, subcategories, modObj, catObj);
                          } else if (row.type === 'subcategory') {
                            const modObj = localModuleData.objects.find((m: any) => Array.isArray(m.categories) && m.categories.some((cat: any) => Array.isArray(cat.types) && cat.types.some((type: any) => 'subcategory-' + m.name + '-' + cat.name + '-' + type.name === row.typeKey)));
                            let typeObj = null;
                            let catObj = null;
                            if (modObj && Array.isArray(modObj.categories)) {
                              for (const cat of modObj.categories) {
                                if (Array.isArray(cat.types)) {
                                  typeObj = cat.types.find((type: any) => 'subcategory-' + modObj.name + '-' + cat.name + '-' + type.name === row.typeKey);
                                  if (typeObj) {
                                    catObj = cat;
                                    break;
                                  }
                                }
                              }
                            }
                            setLocalSelectedTypes((prev) => prev.filter((type) => 'subcategory-' + type.moduleName + '-' + type.categoryName + '-' + type.name !== row.typeKey));
                            onRemove(row.typeKey, typeObj, modObj, catObj);
                          }
                        }}
                      >
                        <CloseIcon sx={{ color }} />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell align="left" sx={{ border: 'none', px: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mr: 0.5 }}>
                        <Checkbox
                          checked={perms.male}
                          onChange={() => handleGenderChange(row.typeKey, 'male')}
                          icon={CustomCheckboxUncheckedIcon}
                          checkedIcon={CustomCheckboxIcon}
                          sx={{
                            p: 0,
                            mx: 0.5,
                            cursor: (row.name === 'Embryology' || row.name === 'Oocyte Grading' || row.name === 'Embryo Culture' || row.name === 'PGT') ? 'not-allowed' : 'pointer',
                            pointerEvents: (row.name === 'Embryology' || row.name === 'Oocyte Grading' || row.name === 'Embryo Culture' || row.name === 'PGT') ? 'auto' : 'auto',
                            '&.Mui-disabled': { cursor: 'not-allowed', pointerEvents: 'auto' }
                          }}
                        />
                        <Typography sx={{ fontWeight: 500, fontSize: 16 }}>{t('UserRights.Male')}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Checkbox
                          checked={perms.female}
                          onChange={() => handleGenderChange(row.typeKey, 'female')}
                          icon={CustomCheckboxUncheckedIcon}
                          checkedIcon={CustomCheckboxIcon}
                          sx={{
                            p: 0,
                            mx: 0.5,
                            cursor: (row.name === 'Andrology' || row.name === 'Semen Analysis' ||row.name === 'Surgical Sperm Retrival' || row.name === 'Semen Processing' || row.name === 'Semen Freezing' || row.name === 'IUI' || row.name === 'Semen Thawing') ? 'not-allowed' : 'pointer',
                            pointerEvents: (row.name === 'Andrology' || row.name === 'Semen Analysis' ||row.name === 'Surgical Sperm Retrival'|| row.name === 'Semen Processing' || row.name === 'Semen Freezing' || row.name === 'IUI' || row.name === 'Semen Thawing') ? 'auto' : 'auto',
                            '&.Mui-disabled': { cursor: 'not-allowed', pointerEvents: 'auto' }
                          }}
                          disabled={row.name === 'Andrology'||row.name === 'Semen Analysis' || row.name === 'Semen Processing' || row.name === 'Semen Freezing' || row.name === 'IUI'|| row.name === 'Semen Thawing'}
                        />
                        <Typography sx={{ fontWeight: 500, fontSize: 16 }}>{t('UserRights.Female')}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="left" sx={{ border: 'none', px: 1 }}>
                    <Checkbox
                      checked={permissionKeys.every((perm) => perms.permissions[perm])}
                      indeterminate={false}
                      onChange={() => handleAllChange(row.typeKey)}
                      icon={CustomCheckboxUncheckedIcon}
                      checkedIcon={CustomCheckboxIcon}
                      sx={{ p: 0, color: '#4CAF50', '&.Mui-checked': { color: '#4CAF50', bgcolor: '#E6F7EA' } }}
                    />
                  </TableCell>
                  {permissionKeys.map((perm) => (
                    <TableCell key={perm} align="left" sx={{ border: 'none', px: 1 }}>
                      <Checkbox
                        checked={perms.permissions[perm] || false}
                        onChange={() => handlePermissionChange(row.typeKey, perm)}
                        icon={CustomCheckboxUncheckedIcon}
                        checkedIcon={CustomCheckboxIcon}
                        sx={{ p: 0, color: '#4CAF50', '&.Mui-checked': { color: '#4CAF50', bgcolor: '#E6F7EA' } }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {allRows.length > 0 && (

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 3 }}>
          {( canEdit || canAdd) && (

            <Box
              component="button"
              onClick={handleGrantAccess}

              sx={{
                bgcolor: '#222',
                color: '#fff',
                fontWeight: 700,
                fontFamily: 'Montserrat',
                fontSize: 20,
                borderRadius: 3,
                px: 6,
                py: 1,
                border: 'none',
                cursor: 'pointer',
                transition: 'background 0.2s',
                '&:hover': { bgcolor: '#111' },
              }}
            >
              {t('UserRights.GrantAccess')}
            </Box>
          )}

        </Box>
      )}
    </Box>
  );
};

export default UserRightsTable;
